import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useColorModeValue,
  ButtonGroup,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import InvoicesTable from "./components/InvoicesTable";
import { columnsDataInvoices } from "./variables/columnsData";
import Pagination from "components/pagination";
import CreateInvoiceModal from "components/modals/CreateInvoiceModal";
import { useToastNotification } from 'utils/toast-utils';
import InvoiceDetails from "./components/InvoiceDetails";
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserInvoices, fetchUserDraftInvoices } from '../../../features/invoices/actions';
import { clearSelectedInvoice } from '../../../features/invoices/slice';

const Invoices = () => {
  const dispatch = useDispatch();
  const { invoices, selectedInvoice, loading, draftInvoices } = useSelector((state) => state.invoices);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const bgInput = useColorModeValue("transparent", "navy.900");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { showToast } = useToastNotification();
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingDraft, setEditingDraft] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);

  const filteredInvoices = useMemo(() => {
    switch (activeTab) {
      case "Draft":
        return draftInvoices || [];
      // case "Closed":
      //   return (invoices || []).filter(invoice => invoice.status === "CLOSED");
      case "All":
      default:
        return invoices || [];
    }
  }, [invoices, draftInvoices, activeTab]);

  const totalItems = filteredInvoices.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredInvoices.slice(startIndex, endIndex);
  }, [filteredInvoices, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      if (activeTab === "Draft") {
        await dispatch(fetchUserDraftInvoices());
      } else {
        const statusParams = activeFilters.length > 0 
          ? activeFilters.map(status => `status=${status}`).join('&')
          : '';
        
        await dispatch(fetchUserInvoices({
          page: currentPage,
          limit: 0,
          searchTerm,
          statusFilters: activeFilters
        }));
      }
    };

    const debounced = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(debounced);
  }, [dispatch, currentPage, itemsPerPage, searchTerm, activeTab, activeFilters]);

  const handlePageChange = useCallback((data, page, limit) => {
    setCurrentPage(page);
    setItemsPerPage(limit);
  }, []);

  const handleCreateInvoice = async (invoiceData) => {
    showToast('Success', 'Invoice created successfully', 'success');
    setIsCreateModalOpen(false);
  };

  const handleRowClick = (invoice) => {
    dispatch({ type: 'invoices/setSelectedInvoice', payload: invoice });
    setShowDetails(true);
  };

  const handleBack = () => {
    dispatch(clearSelectedInvoice());
    setShowDetails(false);
  };

  const handleEditDraft = (draft) => {
    setEditingDraft(draft);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingDraft(null);
    setIsCreateModalOpen(false);
  };

  const handleFilterApply = (selectedStatuses) => {
    setActiveFilters(selectedStatuses);
  };

  return (
    <Card borderRadius="0" p="0">
      {showDetails ? (
        <InvoiceDetails
          invoiceData={selectedInvoice}
          onBack={handleBack}
        />
      ) : (
        <Flex direction="column">
          <Flex justify="space-between" align="center" mb={4}>
            <Text color={textColor} fontSize="30px" fontWeight="700" lineHeight="32px">
              Invoice
            </Text>
            <Flex gap={4}>
              <Input
                placeholder="Search"
                borderRadius="4px"
                size="md"
                width="218px"
                height="40px"
                bg={bgInput}
                _placeholder={{ color: "gray.400" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                color="white"
                colorScheme="purple"
              >
                Create Invoice
              </Button>
            </Flex>
          </Flex>

          <Card borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
            <InvoicesTable
              columnsData={columnsDataInvoices}
              tableData={currentPageData}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onRowClick={handleRowClick}
              onEditDraft={handleEditDraft}
              onFilterApply={handleFilterApply}
            />
            <Flex justify="space-between" align="center" borderColor="gray.200">
              <Pagination
                data={filteredInvoices}
                onPageChange={handlePageChange}
                initialItemsPerPage={itemsPerPage}
                currentPage={currentPage}
                totalItems={totalItems}
                totalPages={totalPages}
                hasMoreData={currentPage < totalPages}
              />
            </Flex>
          </Card>
        </Flex>
      )}
      <CreateInvoiceModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        initialValues={editingDraft}
        isEditingDraft={!!editingDraft}
      />
    </Card>
  );
};

export default Invoices;
